// TFVF Playbook — searchable, section-grouped view of the entire
// approved Total Fusion training corpus (Sales Playbook, Approved Words,
// Objection Playbook, Green Brain guide, Quick Reference, Manuals).
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Search, BookOpen, ChevronRight } from 'lucide-react';
import { API, authHeaders, renderMarkdown } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfPlaybook = () => {
  const { token } = useAuth();
  const [groups, setGroups] = useState({});
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback((query = '') => {
    setLoading(true);
    axios.get(`${API}/tfvf/playbook`, { headers: authHeaders(token), params: query ? { q: query } : {} })
      .then(r => setGroups(r.data?.groups || {}))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const groupOrder = ['Sales Playbook','Approved Words','Green Brain','Objection Playbook','Quick Reference','Staff Manual','Manager Coaching'];
  const totalCount = useMemo(() => Object.values(groups).reduce((s, arr) => s + arr.length, 0), [groups]);

  return (
    <div className="space-y-5" data-testid="tfvf-playbook-page">
      <div className="tf-card rounded-xl p-5">
        <p className="tf-eyebrow">Searchable playbook</p>
        <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">Approved Total Fusion Language, Questions & Guidance</h2>
        <p className="text-xs text-[color:var(--tf-muted)] mt-1">
          Every section below comes straight from the approved Total Fusion training documents. Use search to find a specific script, objection response or Green Brain step.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="mt-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--tf-muted)]" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="e.g. rule of 3, green brain, let's get started, price objection"
              className="w-full bg-white border border-[color:var(--tf-forest)]/15 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:border-[color:var(--tf-gold)]"
              data-testid="tfvf-playbook-search"
            />
          </div>
          <button type="submit" className="tf-btn-primary rounded-lg px-4 py-2 text-sm font-semibold">Search</button>
        </form>
        <p className="text-xs text-[color:var(--tf-muted)] mt-2">{totalCount} sections{q && ` matching "${q}"`}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>
      ) : totalCount === 0 ? (
        <p className="text-center text-[color:var(--tf-muted)] italic py-8">No matching sections. Try a broader term.</p>
      ) : (
        groupOrder.filter(g => groups[g]?.length).map(group => (
          <section key={group} className="tf-card rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-[color:var(--tf-parchment)] border-b border-[color:var(--tf-forest)]/10">
              <p className="tf-eyebrow flex items-center gap-2"><BookOpen className="w-3 h-3" /> {group}</p>
              <p className="text-xs text-[color:var(--tf-muted)] mt-0.5">{groups[group].length} section{groups[group].length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y divide-[color:var(--tf-forest)]/10">
              {groups[group].map(s => {
                const open = openId === s.id;
                return (
                  <div key={s.id} data-testid={`playbook-section-${s.id}`}>
                    <button
                      onClick={() => setOpenId(open ? null : s.id)}
                      className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[color:var(--tf-parchment)]/50 transition-colors"
                    >
                      <ChevronRight className={`w-4 h-4 text-[color:var(--tf-forest)]/50 transition-transform ${open ? 'rotate-90' : ''}`} />
                      <span className="text-sm font-semibold text-[color:var(--tf-forest)] flex-1">{s.title}</span>
                      <span className="text-[10px] text-[color:var(--tf-muted)] italic">{s.source_doc}</span>
                    </button>
                    {open && (
                      <div className="px-5 pb-4 pt-1 text-sm">{renderMarkdown(s.body_markdown)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default TfvfPlaybook;
